import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@treasuryspring/spring-ui';
import { useCallback, useState } from 'react';

import { useRequestPdfMutation } from '@/api/mutations';
import { isEmailValid } from '@/utils/helper-functions';
import { useAppStore } from '@/stores/useAppStore';

interface PdfModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function PdfModal({ isOpen, onOpenChange }: PdfModalProps) {
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [validationError, setValidationError] = useState('');
  const { mutate: requestPdf, isPending } = useRequestPdfMutation();
  const { token } = useAppStore();

  const closeModal = useCallback(() => {
    onOpenChange(false);
    setClientName('');
    setClientEmail('');
  }, [onOpenChange]);

  const validateAndRequest = useCallback(async () => {
    setValidationError('');
    if (isEmailValid(clientEmail) && clientName !== '') {
      requestPdf(
        {
          clientName,
          clientEmail,
          token,
        },
        { onSuccess: closeModal }
      );
    } else {
      setValidationError('Please enter a valid name and email address');
    }
  }, [clientEmail, clientName, closeModal, requestPdf, token]);

  return (
    <Dialog open={isOpen} onOpenChange={closeModal}>
      <DialogContent className="w-[30rem] max-w-10/12">
        <DialogHeader>
          <DialogTitle>Download PDF</DialogTitle>
          <DialogDescription>
            To download a PDF copy of our rates, please provide us with your
            name and email address
          </DialogDescription>
        </DialogHeader>
        <Input
          placeholder="Name"
          type="text"
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
          disabled={isPending}
        />
        <Input
          placeholder="Email Address"
          type="email"
          value={clientEmail}
          onChange={(e) => setClientEmail(e.target.value)}
          disabled={isPending}
        />
        {validationError && (
          <p className="text-on-error -mt-xxs">{validationError}</p>
        )}
        <DialogFooter>
          <Button variant="secondary" onClick={closeModal} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={validateAndRequest}
            disabled={isPending}
            loading={isPending}
          >
            Download PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
